import { useCallback, useEffect, useRef, useState } from 'react';
import { posApi } from '../api/endpoints';
import type { ConnectionState } from '../lib/connectivity';
import { runInvoiceQueue, type InvoiceOutcome } from './invoiceQueue';
import type { PosStore } from './store';

/** Stand eines Kaufbelegs zu einem Verkauf, wie ihn die Kasse anzeigt; `seq` ist die Nummer des Verkaufs. */
export type InvoiceState = (
  { kind: 'waiting' } | { kind: 'blocked'; message: string } | InvoiceOutcome
) & { seq?: number | undefined };

/** Wurde während eines Laufs ein weiterer angefordert? (Als Funktion, weil der Wert aus Ereignissen kommt.) */
const repeatFlag = (flag: { current: boolean }): boolean => flag.current;

/** Wie oft ein Lauf von selbst wiederholt wird, solange noch etwas wartet (Pausen nach „nicht gefunden“). */
const RETRY_EVERY_MS = 30_000;
/** Nach so vielen Läufen in Folge mit Ablehnung des Servers (Anmeldung, Serverfehler) oder nach 15 Minuten wird es angezeigt. */
export const BLOCKED_AFTER_RUNS = 3;
export const BLOCKED_AFTER_MS = 15 * 60_000;

interface Options {
  store: PosStore | null;
  /**
   * Kaufbeleg ist eingeschaltet (Fähigkeit des Servers) und die Kasse hat ein Konto (keine Gerätesitzung). `null`: Der Stand
   * ist noch nicht gelesen, dann wird weder gesendet noch gelöscht. `false`: alle wartenden Anfragen samt Adresse werden gelöscht.
   */
  enabled: boolean | null;
  /** Die Kasse ist gesperrt oder abgekoppelt: wartende Anfragen werden samt Adresse gelöscht. */
  revoked: boolean;
  connection: ConnectionState;
  /** Zählt hoch, wenn ein Abgleich Verkäufe bestätigt haben kann. */
  version: number;
  eventId: string;
  registerId: string | undefined;
}

/**
 * Wartende Kaufbelege der Kasse: Anfragen liegen lokal, bis der Verkauf bestätigt und die Verbindung da ist; dann geht die
 * Anfrage hinaus und die Adresse wird vom Gerät gelöscht. Läuft nur, solange die Kassenseite offen ist.
 */
export function useInvoices(options: Options): {
  states: Readonly<Record<string, InvoiceState>>;
  request: (saleClientUuid: string, email: string, seq: number | undefined) => Promise<void>;
  dismiss: (saleClientUuid: string) => void;
} {
  const [states, setStates] = useState<Record<string, InvoiceState>>({});
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  });
  const mounted = useRef(false);
  const running = useRef(false);
  const again = useRef(false);
  const retryNeeded = useRef(false);
  const seqs = useRef(new Map<string, number | undefined>());
  const trouble = useRef<{ runs: number; sinceMs: number } | null>(null);

  const run = useCallback(async (): Promise<void> => {
    if (running.current) {
      again.current = true;
      return;
    }
    running.current = true;
    try {
      do {
        again.current = false;
        const { store, enabled, revoked, connection, eventId, registerId } = optionsRef.current;
        if (store === null) return;
        for (const entry of await store.invoiceRequests()) {
          seqs.current.set(entry.saleClientUuid, entry.registerSeq);
        }
        const purge = enabled === false || revoked;
        const canSend =
          enabled === true && !revoked && connection !== 'offline' && registerId !== undefined;
        const nowMs = Date.now();
        const result = await runInvoiceQueue(
          store,
          async (saleClientUuid, email, requestId) => {
            await posApi.sendInvoice(eventId, registerId ?? '', saleClientUuid, email, requestId);
          },
          { canSend, purge, nowMs },
        );
        // Wiederholte Ablehnung durch den Server wird sichtbar, statt endlos „Wird gesendet“ zu zeigen
        if (result.trouble === undefined) trouble.current = null;
        else {
          const current = trouble.current ?? { runs: 0, sinceMs: nowMs };
          trouble.current = { runs: current.runs + 1, sinceMs: current.sinceMs };
        }
        const blocked =
          result.trouble !== undefined &&
          trouble.current !== null &&
          (trouble.current.runs >= BLOCKED_AFTER_RUNS ||
            nowMs - trouble.current.sinceMs >= BLOCKED_AFTER_MS);
        const waiting = await store.invoiceRequests();
        if (!mounted.current) return;
        setStates((current) => {
          const next: Record<string, InvoiceState> = { ...current };
          for (const entry of waiting) {
            next[entry.saleClientUuid] =
              blocked && result.trouble !== undefined
                ? {
                    kind: 'blocked',
                    message: result.trouble,
                    seq: entry.registerSeq,
                  }
                : { kind: 'waiting', seq: entry.registerSeq };
          }
          for (const done of result.finished) {
            next[done.saleClientUuid] = {
              ...done.outcome,
              seq: seqs.current.get(done.saleClientUuid),
            };
          }
          return next;
        });
        retryNeeded.current = waiting.length > 0;
      } while (repeatFlag(again));
    } catch {
      // Speicherfehler: die Anfragen bleiben stehen, der nächste Lauf versucht es erneut
    } finally {
      running.current = false;
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const { store, enabled, revoked, connection, version, registerId } = options;
  useEffect(() => {
    void run();
  }, [run, store, enabled, revoked, connection, version, registerId]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (retryNeeded.current) void run();
    }, RETRY_EVERY_MS);
    return () => {
      clearInterval(timer);
    };
  }, [run]);

  const request = useCallback(
    async (saleClientUuid: string, email: string, seq: number | undefined): Promise<void> => {
      const current = optionsRef.current.store;
      if (current === null) return;
      seqs.current.set(saleClientUuid, seq);
      try {
        await current.queueInvoice({
          saleClientUuid,
          email,
          requestId: crypto.randomUUID(),
          registerSeq: seq,
          createdAtMs: Date.now(),
          notFound: 0,
        });
      } catch {
        setStates((previous) => ({
          ...previous,
          [saleClientUuid]: {
            kind: 'failed',
            message:
              'Der Kaufbeleg konnte nicht vorgemerkt werden. Die Adresse wurde vom Gerät gelöscht.',
            seq,
          },
        }));
        return;
      }
      setStates((previous) => ({ ...previous, [saleClientUuid]: { kind: 'waiting', seq } }));
      retryNeeded.current = true;
      void run();
    },
    [run],
  );

  const dismiss = useCallback((saleClientUuid: string): void => {
    setStates((previous) => {
      const { [saleClientUuid]: removed, ...rest } = previous;
      return removed?.kind === 'waiting' || removed?.kind === 'blocked' ? previous : rest;
    });
  }, []);

  return { states, request, dismiss };
}
